import {
  DataGrid,
  GridPagination as MuiDataGridPagination,
} from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  Grid2 as Grid,
  FormControl,
} from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadIcon from "@mui/icons-material/Download";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import { packageColumns } from "./permitPackageTabs/permitTypeColumns";
import {
  noRecordTitle,
  packageRowDetailsEmptyTitle,
} from "../../data/mappings/permitPackageMappings";
import {
  formatCity,
  formatDateFromISO,
  formatDateFromUnix,
  toTitleCase,
} from "./permitComponentHelper";
import { isCurrentFormNOC } from "../permitForms/permitFormHelpers";
import PermitPackageMiddleContent from "./middleContent/PermitPackageMiddleContent";
import CustomDocumentMiddleContent from "./middleContent/CustomDocumentMiddleContent";
import PermitPackageRightContent from "./rightContent/PermitPackageRightContent";
import CustomDocumentRightContent from "./rightContent/CustomDocumentRightContent";
import SelectDownloadFileSIzeModal from "../modals/SelectDownloadFileSIzeModal";
import {
  clearNewTempCustomerPermitData,
  getCustomerPackages,
  handleDownloadNoc,
  updateCustomerPackage,
} from "../../store/actions/dataActions";
import {
  setCreatingDocTitle,
  setDocumentImage,
  setLeftValue,
  setMyPermitsSnackOpen,
  setNumCreated,
  setPage,
  setPageSize,
  setPermitLoadings,
  setRightValue,
  setRowCount,
  setSelectedRow,
  setSelectedPermitType,
} from "../../store/actions/uiActions/myPermitsActions";

const CustomersPackagesGrid = () => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { selectedRow, leftValue, selectedPermitType } = useSelector(
    (state) => state.myPermitsComponent,
  );
  const { myPermitsSnackOpen, myPermitsSnackText } = useSelector(
    (state) => state.myPermitsComponent.permits,
  );
  const { dataLoading, ppDownloading, cancelPermitLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );
  const { page, rowCount } = useSelector(
    (state) => state.myPermitsComponent.permits.pagination,
  );
  const newTempCustomerPermitData = useSelector(
    (state) => state.nocData?.newTempCustomerPermitData,
  );
  const rows = useSelector((state) => state.nocData?.customerPackages || []);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const desktopSize = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const isMobilePermits = useMediaQuery("(max-width:899px)");

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectionModel, setSelectionModel] = useState([]);
  const [recordLoading, setRecordLoading] = useState(false);
  const [currentLeftNotarized, setCurrentLeftNotarized] = useState(false);
  const [currentLeftInProgress, setCurrentLeftInProgress] = useState(false);
  const [permitOptions, setPermitOptions] = useState([]);
  const [mobileDetailsView, setMobileDetailsView] = useState("docs");
  const [fileSizeModalOpen, setFileSizeModalOpen] = useState(false);

  const skipNextPaginationRef = useRef(false);
  const packageDetailRef = useRef(null);
  const selectedRowIdRef = useRef(null);

  useEffect(() => {
    selectedRowIdRef.current = selectedRow?.id || null;
  }, [selectedRow?.id]);

  const fetchRows = useCallback(
    async (newPage = page, newRowsPerPage = rowsPerPage) => {
      try {
        await dispatch(setPermitLoadings({ dataLoading: true }));
        const result = await dispatch(
          getCustomerPackages({
            newPage,
            newPageSize: newRowsPerPage,
          }),
        );

        if (result?.success) {
          const nextRows = Array.isArray(result?.rows) ? result.rows : [];
          dispatch(setPage(newPage));
          dispatch(setPageSize(newRowsPerPage));
          dispatch(setRowCount(Number(result?.data || 0)));

          if (selectedRowIdRef.current) {
            const refreshedSelected = nextRows.find(
              (row) => row.id === selectedRowIdRef.current,
            );
            if (refreshedSelected) {
              dispatch(setSelectedRow(refreshedSelected));
            }
          }
        } else {
          dispatch(setRowCount(0));
          dispatch(
            setMyPermitsSnackOpen({
              isOpen: true,
              snackText: result?.error || "Failed to load customer packages.",
            }),
          );
        }
      } catch (error) {
        dispatch(setRowCount(0));
        dispatch(
          setMyPermitsSnackOpen({
            isOpen: true,
            snackText:
              error?.response?.data?.message ||
              "Failed to load customer packages.",
          }),
        );
      } finally {
        await dispatch(setPermitLoadings({ dataLoading: false }));
      }
    },
    [dispatch, page, rowsPerPage],
  );

  useEffect(() => {
    fetchRows(0, rowsPerPage);
  }, [fetchRows, rowsPerPage]);

  useEffect(() => {
    if (!newTempCustomerPermitData) return;
    const tempId =
      newTempCustomerPermitData?.task_id ||
      newTempCustomerPermitData?.cst_packageID;
    const newRow = rows.find(
      (row) => (row?.task_id || row?.cst_packageID || row?.id) === tempId,
    );

    if (!newRow) return;

    dispatch(setSelectedPermitType(newRow?.permit_type));
    dispatch(setSelectedRow(newRow));
    dispatch(setLeftValue(0));
    dispatch(
      setCreatingDocTitle(
        newRow?.selected_documents?.[0]?.label || "Notice of Commencement",
      ),
    );
    dispatch(setDocumentImage(newRow?.selected_documents?.[0]?.img));
    setSelectionModel([newRow.id]);
    dispatch(clearNewTempCustomerPermitData());
  }, [dispatch, newTempCustomerPermitData, rows]);

  useEffect(() => {
    if (selectedRow?.id) {
      setSelectionModel([selectedRow.id]);
    }
  }, [selectedRow?.id]);

  useEffect(() => {
    let nextPermitOptions = [];
    if (selectedRow?.permit_type)
      nextPermitOptions.push(selectedRow.permit_type);
    if (selectedRow?.additional_permits) {
      nextPermitOptions.push(...Object.keys(selectedRow.additional_permits));
    }
    nextPermitOptions = [...new Set(nextPermitOptions)].filter(Boolean);
    setPermitOptions(nextPermitOptions);

    if (
      nextPermitOptions.length > 0 &&
      !nextPermitOptions.includes(selectedPermitType)
    ) {
      dispatch(setSelectedPermitType(nextPermitOptions[0]));
    }
  }, [dispatch, selectedPermitType, selectedRow]);

  const getSelectedDocumentsForPermitType = useCallback(
    (permitType) => {
      if (!selectedRow || !permitType) return [];
      if (permitType === selectedRow.permit_type) {
        return Array.isArray(selectedRow.selected_documents)
          ? selectedRow.selected_documents
          : [];
      }

      const additionalPermit = selectedRow?.additional_permits?.[permitType];
      if (!additionalPermit) return [];
      if (Array.isArray(additionalPermit)) {
        return Array.isArray(additionalPermit?.[0]?.selected_documents)
          ? additionalPermit[0].selected_documents
          : [];
      }
      return Array.isArray(additionalPermit?.selected_documents)
        ? additionalPermit.selected_documents
        : [];
    },
    [selectedRow],
  );

  useEffect(() => {
    if (selectedRow) {
      const createdCount =
        getSelectedDocumentsForPermitType(selectedPermitType)?.filter(
          (doc) => doc.created === true,
        ).length || 0;
      dispatch(setNumCreated(createdCount));
    }
  }, [
    dispatch,
    getSelectedDocumentsForPermitType,
    selectedPermitType,
    selectedRow,
  ]);

  useEffect(() => {
    const isNOC = isCurrentFormNOC(selectedRow, selectedPermitType, leftValue);
    const notarized =
      selectedRow?.notarized ||
      (isNOC && selectedRow?.noc_notarized === true) ||
      (leftValue >= 1 && selectedRow?.pp_notarized === true)
        ? true
        : false;
    setCurrentLeftNotarized(notarized);

    const notarizingDocs =
      selectedRow?.processing_permit?.notarization?.notarizing_docs;
    const finalized = selectedRow?.processing_permit?.notarization?.finalized;
    const started = selectedRow?.processing_permit?.notarization?.started;
    const inProgress =
      !notarized &&
      ((notarizingDocs === 0 && started === true && !finalized) ||
        (notarizingDocs === 1 && isNOC && started === true && !finalized) ||
        (notarizingDocs === 2 && !isNOC && started === true && !finalized));

    setCurrentLeftInProgress(Boolean(inProgress));
  }, [leftValue, selectedPermitType, selectedRow]);

  const handleLeftChange = useCallback(
    (event, newValue, shouldMoveToStatus = false) => {
      dispatch(setLeftValue(newValue));
      if (shouldMoveToStatus) setMobileDetailsView("status");
    },
    [dispatch],
  );

  const handlePermitTypeChange = useCallback(
    (newPermitType) => {
      if (permitOptions.includes(newPermitType)) {
        dispatch(setSelectedPermitType(newPermitType));
        dispatch(setLeftValue(0));
      }
    },
    [dispatch, permitOptions],
  );

  const handleRowClick = useCallback(
    (params) => {
      if (cancelPermitLoading) return;
      const row = params.row;
      if (selectedRow?.id === row?.id) return;

      dispatch(setSelectedRow(row));
      setSelectionModel([row.id]);
      dispatch(setPermitLoadings({ tempLoading: false }));

      const rowDocs = row?.selected_documents || [];
      let newLeftValue = 0;
      for (let i = 0; i < rowDocs.length; i++) {
        if (!rowDocs[i]?.created) {
          newLeftValue = i;
          break;
        }
        newLeftValue = i + 1;
      }
      if (newLeftValue >= rowDocs.length) {
        newLeftValue = Math.max(0, rowDocs.length - 1);
      }
      if (rowDocs.length > 0) {
        dispatch(setLeftValue(newLeftValue));
      }

      const newRightValue =
        (row?.permit_package_created ||
          (row?.noc_created && row?.selected_documents?.length === 1)) &&
        !row?.notarized
          ? 2
          : row?.notarized && !row?.recorded
            ? 3
            : 0;
      if (!row?.cst_packageID) {
        dispatch(setRightValue(newRightValue));
      } else {
        dispatch(setRightValue(0));
      }

      setTimeout(() => {
        packageDetailRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    },
    [cancelPermitLoading, dispatch, selectedRow?.id],
  );

  const handlePaginationModelChange = useCallback(
    (model) => {
      if (skipNextPaginationRef.current) {
        skipNextPaginationRef.current = false;
        return;
      }
      dispatch(setSelectedRow(null));
      fetchRows(model.page, model.pageSize);
    },
    [dispatch, fetchRows],
  );

  const handleStartRecord = useCallback(async () => {
    try {
      setRecordLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/start-manual-recording`,
        { task_id: selectedRow?.task_id, currentSession },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        const updatedRow = {
          id: resp?.data?.data?.task_id || resp?.data?.data?.cst_packageID,
          ...resp?.data?.data,
        };
        dispatch(updateCustomerPackage(updatedRow));
        dispatch(setSelectedRow(updatedRow));
      }
    } finally {
      setRecordLoading(false);
    }
  }, [currentSession, dispatch, selectedRow?.task_id]);

  const snackHandleClose = () => {
    dispatch(setMyPermitsSnackOpen({ isOpen: false, snackText: "" }));
  };

  const statusItem = useCallback((label, value, allowNA = false) => {
    const isNA = allowNA && value === null;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
        <Typography sx={{ fontSize: "0.68rem", color: "rgba(0, 0, 0, 0.65)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography
            sx={{ fontSize: "0.68rem", color: "rgba(0, 0, 0, 0.65)" }}
          >
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "0.95rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "0.95rem" }} />
        )}
      </Box>
    );
  }, []);

  const compactStatusChip = (label, value, allowNA = false) => {
    const isNA = allowNA && value === null;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "1rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "1rem" }} />
        )}
      </Box>
    );
  };

  const dataGridColumns = useMemo(() => {
    const columns = packageColumns?.[0] || [];
    return columns.filter((column) => column?.field !== "created_by");
  }, []);

  const permitColumnsSmall = useMemo(() => {
    const packageColumn = {
      field: "package_name",
      headerName: "Packages",
      flex: 1.3,
      sortable: false,
      renderCell: (params) => {
        const isCustomDoc = Boolean(params?.row?.cst_packageID);
        const packageName = isCustomDoc
          ? "Custom Documents"
          : params?.row?.package_name || "N/A";
        const projectAddress = isCustomDoc
          ? params?.row?.document_location?.project_address || "N/A"
          : params?.row?.project_address || "N/A";
        const county = isCustomDoc
          ? params?.row?.document_location?.county || "N/A"
          : params?.row?.county || "N/A";
        const city = isCustomDoc ? "N/A" : formatCity(params?.row?.city);

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              py: 0.7,
              pl: 1,
              pr: 0.5,
              width: "100%",
            }}
          >
            <Typography
              sx={{ fontSize: "0.83rem", fontWeight: 600, lineHeight: 1.2 }}
            >
              {packageName}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "rgba(0, 0, 0, 0.62)",
                lineHeight: 1.2,
                mt: 0.2,
              }}
            >
              {projectAddress}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "rgba(0, 0, 0, 0.62)",
                lineHeight: 1.2,
                mt: 0.2,
              }}
            >
              {county} • {city}
            </Typography>
          </Box>
        );
      },
    };

    return [
      packageColumn,
      {
        field: "status",
        headerName: "",
        renderHeader: () => null,
        headerClassName: "tablet-empty-header",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const isCustomDoc = Boolean(params?.row?.cst_packageID);
          let initiatedDate = "N/A";

          if (isCustomDoc) {
            const canNotarize = params?.row?.document?.actions?.notarize;
            const canRecord = params?.row?.document?.actions?.record;
            const startedAt =
              (canNotarize && canRecord) || canNotarize
                ? params?.row?.processing_permit?.notarization?.started_at
                : canRecord
                  ? params?.row?.processing_permit?.recording?.started_at
                  : null;
            initiatedDate = formatDateFromISO(startedAt);
          } else {
            initiatedDate = formatDateFromUnix(
              params?.row?.permit_package_added_at,
            );
          }

          const nocCreated = isCustomDoc
            ? null
            : Boolean(params?.row?.noc_created);
          const hasMultipleDocs =
            (params?.row?.selected_documents?.length || 0) > 1;
          const ppCreated = isCustomDoc
            ? null
            : Boolean(params?.row?.permit_package_created && hasMultipleDocs);
          const notarized = isCustomDoc
            ? params?.row?.document?.actions?.notarize
              ? Boolean(params?.row?.notarized)
              : null
            : Boolean(params?.row?.notarized);
          const recorded = isCustomDoc
            ? params?.row?.document?.actions?.record
              ? Boolean(
                  params?.row?.processing_permit?.recording?.recorded ||
                    params?.row?.recorded,
                )
              : null
            : Boolean(params?.row?.recorded);

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "center",
                textAlign: "right",
                py: 0.7,
                pr: 1,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "rgba(0, 0, 0, 0.62)",
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {initiatedDate}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.6,
                  justifyContent: "flex-end",
                }}
              >
                {statusItem("NOC", nocCreated, true)}
                {statusItem("PP", ppCreated, true)}
                {statusItem("Not", notarized, true)}
                {statusItem("Rec", recorded, true)}
              </Box>
            </Box>
          );
        },
      },
    ];
  }, [statusItem]);

  const mobileGridColumns = useMemo(
    () => [
      {
        field: "mobile",
        headerName: "Packages",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const isCustomDoc = Boolean(row?.cst_packageID);
          const packageName = isCustomDoc
            ? "Custom Documents"
            : row?.package_name || "N/A";
          const projectAddress = isCustomDoc
            ? row?.document_location?.project_address || "N/A"
            : row?.project_address || "N/A";
          const county = isCustomDoc
            ? row?.document_location?.county || "N/A"
            : row?.county || "N/A";
          const city = isCustomDoc ? "N/A" : formatCity(row?.city);

          let initiatedDate = "N/A";
          if (isCustomDoc) {
            const canNotarize = row?.document?.actions?.notarize;
            const canRecord = row?.document?.actions?.record;
            const startedAt =
              (canNotarize && canRecord) || canNotarize
                ? row?.processing_permit?.notarization?.started_at
                : canRecord
                  ? row?.processing_permit?.recording?.started_at
                  : null;
            initiatedDate = formatDateFromISO(startedAt, "short");
          } else {
            initiatedDate = formatDateFromUnix(
              row?.permit_package_added_at,
              "short",
            );
          }

          const nocCreated = isCustomDoc ? null : Boolean(row?.noc_created);
          const hasMultipleDocs = (row?.selected_documents?.length || 0) > 1;
          const ppCreated = isCustomDoc
            ? null
            : Boolean(row?.permit_package_created && hasMultipleDocs);
          const notarized = isCustomDoc
            ? row?.document?.actions?.notarize
              ? Boolean(row?.notarized)
              : null
            : Boolean(row?.notarized);
          const recorded = isCustomDoc
            ? row?.document?.actions?.record
              ? Boolean(
                  row?.processing_permit?.recording?.recorded || row?.recorded,
                )
              : null
            : Boolean(row?.recorded);

          return (
            <Box
              sx={{
                width: "100%",
                py: 0.6,
                px: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.45,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {packageName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {row?.email?.split("@")[0] || ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      wordBreak: "break-word",
                    }}
                  >
                    {projectAddress}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.74rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.15,
                    }}
                  >
                    {county} • {city}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0,0,0,0.62)",
                    flexShrink: 0,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {initiatedDate}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.0 }}>
                {compactStatusChip("NOC Created", nocCreated, true)}
                {compactStatusChip("Package Built", ppCreated, true)}
                {compactStatusChip("Notarized", notarized, true)}
                {compactStatusChip("Recorded", recorded, true)}
              </Box>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const middleContent = useCallback(
    (display) => {
      if (selectedRow?.cst_packageID) {
        return (
          <CustomDocumentMiddleContent
            setFileSizeModalOpen={setFileSizeModalOpen}
            isMobile={isMobilePermits}
            display={display}
          />
        );
      }

      return (
        <PermitPackageMiddleContent
          currentLeftNotarized={currentLeftNotarized}
          currentLeftInProgress={currentLeftInProgress}
          setFileSizeModalOpen={setFileSizeModalOpen}
          selectedPermitType={selectedPermitType}
          isMobile={isMobilePermits}
          isReadOnlyPage={false}
          display={display}
        />
      );
    },
    [
      currentLeftInProgress,
      currentLeftNotarized,
      selectedPermitType,
      selectedRow?.cst_packageID,
      isMobilePermits,
    ],
  );

  const memoizedRightContent = useCallback(
    (display) => {
      if (!selectedRow) return null;

      return selectedRow?.cst_packageID ? (
        <CustomDocumentRightContent
          recordLoading={recordLoading}
          handleStartRecord={handleStartRecord}
          isMobile={isMobilePermits}
          isReadOnlyPage={false}
          display={display}
        />
      ) : (
        <PermitPackageRightContent
          recordLoading={recordLoading}
          handleStartRecord={handleStartRecord}
          setDocNotaryTypeValue={() => {}}
          docNotaryTypeValue={0}
          selectedPermitType={selectedPermitType}
          isMobile={isMobilePermits}
          isDesktop={desktopSize}
          isReadOnlyPage={false}
          display={display}
        />
      );
    },
    [
      desktopSize,
      handleStartRecord,
      isMobilePermits,
      recordLoading,
      selectedPermitType,
      selectedRow,
    ],
  );

  const mobileDocTabs = useMemo(() => {
    if (!selectedRow || selectedRow?.cst_packageID) return null;

    return (
      <Tabs
        value={leftValue}
        onChange={(e, value) => handleLeftChange(e, value, true)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        orientation="vertical"
        slotProps={{ indicator: { sx: { bottom: 0, height: "3px" } } }}
        sx={{
          width: "100%",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: 1,
          "& .MuiTab-root": {
            textTransform: "none",
            alignItems: "flex-start",
            minHeight: 40,
            px: 1.5,
            marginLeft: 0,
          },
        }}
      >
        {getSelectedDocumentsForPermitType(selectedPermitType)?.map(
          (document, index) => {
            const selectedDocs =
              getSelectedDocumentsForPermitType(selectedPermitType);
            const isEnabled =
              selectedRow?.permit_package_created ||
              document?.created ||
              (selectedDocs?.[index - 1]?.created && !document?.created);

            return (
              <Tab
                disabled={!isEnabled}
                key={document?.name || index}
                label={document?.label}
                disableRipple
              />
            );
          },
        )}
      </Tabs>
    );
  }, [
    getSelectedDocumentsForPermitType,
    handleLeftChange,
    leftValue,
    selectedPermitType,
    selectedRow,
  ]);

  return (
    <>
      <Card
        sx={{
          position: "relative",
          mb: 0,
          pb: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: isMobilePermits ? 1 : 0,
            px: 1,
            pb: 2,
          }}
        >
          <CardHeader
            title={
              mobileSize
                ? "Customer Permit Packages"
                : `${" \u00A0\u00A0"}C U S T O M E R S${" \u00A0\u00A0"}P A C K A G E S`
            }
            sx={{
              flexGrow: 1,
              pt: isMobilePermits ? "16px" : "16px",
              px: 0,
              textAlign: "left",
            }}
          />
        </Box>

        <CardContent
          className="bottomCard-6"
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                borderBottom: "5px solid #d3d3d3",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {rows?.length === 0 && !dataLoading ? (
                <Box
                  sx={{
                    dispay: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: { xs: "250px", xl: "auto" },
                  }}
                >
                  <Typography
                    mt={2}
                    align="center"
                    color="primary"
                    variant="h6"
                  >
                    {noRecordTitle?.[0] || "No customer packages found."}
                  </Typography>
                </Box>
              ) : (
                <DataGrid
                  rows={rows}
                  selectionModel={selectionModel}
                  onSelectionModelChange={(newSelectionModel) =>
                    setSelectionModel(newSelectionModel)
                  }
                  slots={{
                    pagination: (props) => (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.75rem",
                            }}
                          >
                            Rows per page:
                          </Typography>
                          <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                              const newRowsPerPage = e.target.value;
                              skipNextPaginationRef.current = true;
                              setRowsPerPage(newRowsPerPage);
                              dispatch(setPage(0));
                              fetchRows(0, newRowsPerPage);
                            }}
                            size="small"
                            sx={{
                              minWidth: 60,
                              "& .MuiSelect-select": {
                                padding: "4px 8px",
                                fontSize: "0.75rem",
                              },
                            }}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </Box>
                        <MuiDataGridPagination {...props} />
                      </Box>
                    ),
                  }}
                  autoHeight
                  sx={{
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-main": { flex: 1, minHeight: 0 },
                    "& .MuiDataGrid-virtualScroller": { flex: 1, minHeight: 0 },
                    "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      textAlign: "center",
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      ".MuiDataGrid-menuIcon": {
                        display: "none",
                        pointerEvents: "none",
                      },
                      cursor: "default",
                      paddingLeft: 1,
                      paddingRight: 1,
                      margin: 0,
                      backgroundColor: "#333",
                      color: "#fff",
                      borderBottom: "1px solid #ccc",
                    },
                    "& .MuiDataGrid-columnHeaders": { padding: 0, margin: 0 },
                    "& .tablet-empty-header .MuiDataGrid-columnHeaderTitleContainer":
                      {
                        display: "none",
                      },
                    "& .MuiDataGrid-columnHeadersInner": {
                      width: "100%",
                      padding: 0,
                    },
                    border: "1px solid #FCF2F0",
                    "& .MuiDataGrid-cell": {
                      borderColor: tabletSize
                        ? "rgba(0, 0, 0, 0.1)"
                        : "#333333",
                      p: tabletSize ? 0 : undefined,
                    },
                    "& .MuiDataGrid-columnSeparator": { display: "none" },
                    ".MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .selected-row-color": {
                      backgroundColor: "#ebf1f4 !important",
                    },
                    ...(isMobilePermits
                      ? {
                          "& .MuiDataGrid-columnHeaders": { display: "none" },
                          "& .MuiDataGrid-virtualScroller": {
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            touchAction: "pan-y",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            flex: "0 0 auto",
                          },
                          "& .MuiDataGrid-cell": {
                            py: 0,
                            paddingLeft: 0,
                            paddingRight: 2.5,
                            borderTop: "0px !important",
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                          },
                        }
                      : {}),
                  }}
                  columns={
                    isMobilePermits
                      ? mobileGridColumns
                      : tabletSize
                        ? permitColumnsSmall
                        : dataGridColumns
                  }
                  getRowId={(row) => row?.id}
                  onPaginationModelChange={handlePaginationModelChange}
                  loading={dataLoading}
                  paginationMode="server"
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  getRowClassName={(params) =>
                    params?.row?.id === selectedRow?.id
                      ? "selected-row-color"
                      : ""
                  }
                  rowCount={rowCount || 0}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  onRowClick={handleRowClick}
                  pageSizeOptions={[rowsPerPage]}
                  rowHeight={isMobilePermits ? 100 : tabletSize ? 104 : 34}
                  disableColumnFilter={tabletSize}
                />
              )}
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              {!selectedRow ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%",
                    minHeight: "200px",
                  }}
                >
                  <Typography
                    sx={{ textAlign: "center", px: 2 }}
                    variant={mobileSize ? "h6" : "h5"}
                    color="primary"
                  >
                    {rows?.length > 0 ? packageRowDetailsEmptyTitle?.[0] : ""}
                  </Typography>
                </Box>
              ) : (
                <Grid
                  container
                  sx={{ width: "100%", height: "100%", alignItems: "stretch" }}
                  ref={packageDetailRef}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden",
                      flex: 1,
                    }}
                  >
                    {selectedRow?.cst_packageID ? (
                      <Typography
                        variant="h5"
                        sx={{ fontStyle: "italic" }}
                        align="center"
                        mt={0.2}
                        py={0.5}
                      >
                        Custom Documents
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <FormControl
                          variant="standard"
                          sx={{
                            mt: 0.2,
                            ml: isMobilePermits ? 1 : 5,
                            width: "fit-content",
                          }}
                        >
                          <Select
                            value={
                              permitOptions.includes(selectedPermitType)
                                ? selectedPermitType
                                : ""
                            }
                            onChange={(e) =>
                              handlePermitTypeChange(e.target.value)
                            }
                            displayEmpty
                            disableUnderline
                            IconComponent={() => null}
                            renderValue={(value) => (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  fontStyle: "italic",
                                  typography: isMobilePermits ? "h6" : "h5",
                                  lineHeight: 1.1,
                                  px: isMobilePermits ? 0.5 : 0,
                                }}
                              >
                                {value && permitOptions.includes(value)
                                  ? toTitleCase(String(value))
                                  : "Select Permit Type"}
                                <ArrowDropDownIcon
                                  sx={{
                                    ml: 0.5,
                                    fontSize: isMobilePermits ? 22 : 28,
                                  }}
                                />
                              </Box>
                            )}
                          >
                            {(permitOptions || []).map((permit) => (
                              <MenuItem
                                key={permit}
                                value={permit}
                                sx={{ textTransform: "none" }}
                              >
                                {toTitleCase(permit)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    {!selectedRow?.cst_packageID && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 3,
                          py: 0,
                          borderBottom: "1px solid rgba(0,0,0,0.08)",
                          backgroundColor: "rgba(0,0,0,0.02)",
                        }}
                      >
                        <LoadingButton
                          variant="text"
                          disabled={selectedRow?.selected_documents?.every(
                            (doc) => !doc?.created,
                          )}
                          endIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                          size="medium"
                          loading={ppDownloading}
                          onClick={() =>
                            dispatch(handleDownloadNoc("all", "legal"))
                          }
                          sx={{
                            fontWeight: 500,
                            textTransform: "none",
                            color: "primary.main",
                            px: 2,
                            py: 1,
                          }}
                        >
                          Download Package
                        </LoadingButton>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 400, color: "text.secondary" }}
                          >
                            Forms{" "}
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              {getSelectedDocumentsForPermitType(
                                selectedPermitType,
                              )?.filter((doc) => doc.created === true).length ||
                                0}
                            </Box>
                            <Box
                              component="span"
                              sx={{ mx: 0.5, opacity: 0.6 }}
                            >
                              /
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              {getSelectedDocumentsForPermitType(
                                selectedPermitType,
                              )?.length || 0}
                            </Box>
                          </Typography>
                          <Box
                            sx={{
                              width: "1px",
                              height: "16px",
                              backgroundColor: "divider",
                              opacity: 0.4,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 400, color: "text.secondary" }}
                          >
                            Permits{" "}
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              }}
                            >
                              {permitOptions?.length || 0}
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        height: isMobilePermits ? "100%" : "calc(100% - 90px)",
                        flex: isMobilePermits ? 1 : undefined,
                        minHeight: 0,
                        mt: 0,
                        borderTop: "4px solid #d3d3d3",
                        flexDirection: isMobilePermits ? "column" : "row",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: isMobilePermits ? "flex" : "none",
                          flexDirection: "column",
                          minHeight: 0,
                        }}
                      >
                        <Box
                          sx={{
                            px: 1,
                            py: 1,
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                            backgroundColor: "#fff",
                            flex: "0 0 auto",
                          }}
                        >
                          <ToggleButtonGroup
                            value={mobileDetailsView}
                            exclusive
                            fullWidth
                            size="small"
                            onChange={(_, next) => {
                              if (next) setMobileDetailsView(next);
                            }}
                            sx={{
                              backgroundColor: "#f7f7fb",
                              borderRadius: 2,
                              p: 0.3,
                              "& .MuiToggleButton-root": {
                                border: "none",
                                textTransform: "none",
                                fontWeight: 700,
                                flex: 1,
                                py: 0.75,
                              },
                              "& .Mui-selected": {
                                backgroundColor: "#fff",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            {!selectedRow?.cst_packageID && (
                              <ToggleButton value="docs">Docs</ToggleButton>
                            )}
                            <ToggleButton value="status">Status</ToggleButton>
                            <ToggleButton value="details">Details</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <Box
                          sx={{
                            height: "100%",
                            minHeight: 0,
                            backgroundColor: "#fff",
                          }}
                        >
                          {mobileDetailsView === "docs" && (
                            <Box sx={{ width: "100%" }}>{mobileDocTabs}</Box>
                          )}
                          {mobileDetailsView === "status" && (
                            <Box sx={{ width: "100%" }}>
                              {middleContent("flex")}
                            </Box>
                          )}
                          {mobileDetailsView === "details" && (
                            <Box sx={{ width: "100%" }}>
                              {memoizedRightContent("flex")}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {!selectedRow?.cst_packageID && (
                        <Box
                          sx={{
                            display: isMobilePermits ? "none" : "flex",
                            justifyContent: "flex-start",
                            height: "100%",
                            maxHeight: "100%",
                            flexDirection: "column",
                            alignSelf: "stretch",
                            minHeight: 0,
                            overflowY: "auto",
                            maxWidth: { xs: "16rem", xl: "unset" },
                            minWidth: { xs: "unset", xl: "16rem" },
                          }}
                        >
                          <Tabs
                            orientation="vertical"
                            value={leftValue}
                            onChange={handleLeftChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            slotProps={{
                              indicator: { sx: { left: 0, width: "3px" } },
                            }}
                            sx={{
                              alignItems: "flex-start",
                              flex: 1,
                              minHeight: 0,
                              maxHeight: "100%",
                              overflowY: "auto",
                            }}
                          >
                            {getSelectedDocumentsForPermitType(
                              selectedPermitType,
                            )?.map((document, index) => {
                              const selectedDocs =
                                getSelectedDocumentsForPermitType(
                                  selectedPermitType,
                                );
                              const isEnabled =
                                selectedRow?.permit_package_created ||
                                document?.created ||
                                (selectedDocs?.[index - 1]?.created &&
                                  !document?.created);

                              return (
                                <Tab
                                  disabled={!isEnabled}
                                  key={index}
                                  label={
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "flex-start",
                                        textAlign: "left",
                                      }}
                                    >
                                      {document.label}
                                      {selectedRow?.documents?.[document?.name]
                                        ?.download_info?.downloaded === true ? (
                                        <Box
                                          sx={{
                                            backgroundColor: "#004976",
                                            borderRadius: "50%",
                                            height: "18px",
                                            width: "18px",
                                            marginLeft: 1,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "3.5px",
                                          }}
                                        >
                                          <DownloadDoneIcon
                                            style={{
                                              color: "#fff",
                                              fontSize: "16px",
                                            }}
                                          />
                                        </Box>
                                      ) : (selectedDocs?.[index]?.created &&
                                          selectedDocs?.[index]?.name ===
                                            "noc" &&
                                          (selectedRow?.notarized === true ||
                                            selectedRow?.noc_notarized ===
                                              true)) ||
                                        (selectedDocs?.[index]?.name !==
                                          "noc" &&
                                          (selectedRow?.notarized === true ||
                                            selectedRow?.pp_notarized ===
                                              true)) ? (
                                        <Box
                                          sx={{
                                            backgroundColor: green[500],
                                            borderRadius: "50%",
                                            height: "18px",
                                            width: "18px",
                                            marginLeft: 1,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "3.5px",
                                          }}
                                        >
                                          <VerifiedOutlinedIcon
                                            style={{
                                              color: "#fff",
                                              fontSize: "16px",
                                            }}
                                          />
                                        </Box>
                                      ) : selectedRow?.processing_permit?.notarization?.final_documents?.find(
                                          (doc) =>
                                            doc?.name ===
                                            selectedDocs?.[index]?.name,
                                        ) ? (
                                        <Box
                                          sx={{
                                            backgroundColor: grey[500],
                                            borderRadius: "50%",
                                            height: "18px",
                                            width: "18px",
                                            marginLeft: 1,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "3.5px",
                                          }}
                                        >
                                          <CheckIcon
                                            style={{
                                              color: "#fff",
                                              fontSize: "16px",
                                            }}
                                          />
                                        </Box>
                                      ) : selectedDocs?.[index]?.created ? (
                                        <Box
                                          sx={{
                                            backgroundColor: grey[500],
                                            borderRadius: "50%",
                                            height: "18px",
                                            width: "18px",
                                            marginLeft: 1,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "3.5px",
                                          }}
                                        >
                                          <CheckIcon
                                            style={{
                                              color: "#fff",
                                              fontSize: "16px",
                                            }}
                                          />
                                        </Box>
                                      ) : (
                                        <></>
                                      )}
                                    </span>
                                  }
                                  disableRipple
                                  sx={{
                                    py: 0,
                                    alignItems: "flex-start",
                                    marginLeft: "24px",
                                  }}
                                />
                              );
                            })}
                          </Tabs>
                        </Box>
                      )}

                      {middleContent(isMobilePermits ? "none" : "flex")}
                    </Box>
                  </Grid>
                  {memoizedRightContent(isMobilePermits ? "none" : "flex")}
                </Grid>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <SelectDownloadFileSIzeModal
        open={fileSizeModalOpen}
        setOpen={setFileSizeModalOpen}
      />

      <Snackbar
        sx={{ zIndex: 9999999999999 }}
        open={myPermitsSnackOpen}
        autoHideDuration={5000}
        onClose={snackHandleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={snackHandleClose}
          severity="success"
          sx={{
            width: "100%",
            marginTop: "80px",
            backgroundColor: "#388e3c",
            color: "#fff",
            fontWeight: "bold",
          }}
          iconMapping={{ success: <CheckCircleIcon sx={{ color: "#fff" }} /> }}
        >
          {myPermitsSnackText}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(CustomersPackagesGrid);
